import React, { memo, useCallback, useContext } from 'react'

import {
  SettingsOutline, Button, Icon, classnames,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { bool } from 'prop-types'

import DesktopOnlyTooltip from 'Pods/Shared/DesktopOnlyTooltip/DesktopOnlyTooltip'
import { iconSizeNav } from 'Pods/ThreeDStudio/constants'
import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'
import { useThreeDStudioStore } from 'Pods/ThreeDStudio/Store'
import { openSettingsModal } from 'Pods/ThreeDStudio/Store/actions/ui'
import ThreeDStudioPageContext from 'Pods/ThreeDStudio/ThreeDStudioPageContext'

function SettingsButton({ isOpen = true }) {
  const t = useTranslation()
  const studioAccessTier = useThreeDStudioStore(state => state.subscription.studioAccessTier)
  const isSettingsModalOpen = useThreeDStudioStore(state => state.ui.isSettingsModalOpen)
  const { extractingCoreStyles, pageConfigLoaded, userLoggedIn } = useContext(ThreeDStudioPageContext)

  const handleClick = useCallback(() => {
    openSettingsModal()
  }, [])

  // On this fast page login is unknowable at SSR, so render nothing until pageConfig
  // positively confirms a logged-in visitor. Rendering during the pending window flashed
  // the disabled button into the footer, then removed it once pageConfig resolved as
  // anonymous - the majority / CrUX path. Defaulting to hidden accepts a single in-place
  // appearance for logged-in users when pageConfig lands; the footer is a fixed 41px so
  // this adds no layout shift. During extraction the tier is not user-specific, so render.
  if (!extractingCoreStyles && (!pageConfigLoaded || !userLoggedIn)) {
    return null
  }

  const isTierPending = !studioAccessTier && !extractingCoreStyles

  const button = (
    <Button
      type="ghost"
      variant={isOpen ? 'with-icon' : 'only-icon'}
      icon={<Icon svgComponent={SettingsOutline} color="darker" size={iconSizeNav} notSpaced />}
      customClass={classnames('settings-button', !isOpen && 'settings-button--collapsed')}
      onClick={handleClick}
      disabled={isTierPending}
      aria-label={t('sidebar.settings.label')}
      aria-haspopup="dialog"
      aria-expanded={isSettingsModalOpen}
    >
      {isOpen ? (
        <span className="settings-button__label cgt-body-base-semibold">{t('sidebar.settings.label')}</span>
      ) : null}
    </Button>
  )

  if (isOpen) {
    return button
  }

  return (
    <DesktopOnlyTooltip
      content={t('sidebar.settings.label')}
      id="settings-button-tooltip"
      place="right"
      containerClass="settings-button-tooltip"
      className="settings-button-tooltip__trigger"
    >
      {button}
    </DesktopOnlyTooltip>
  )
}

SettingsButton.propTypes = {
  isOpen: bool,
}

export default memo(SettingsButton)
